import { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { SbsComparisonState, SbsJurisdictionResult, Citation, JurisdictionGroup, JURISDICTION_GROUPS as JG } from '../types';

/* ── Jurisdiction Picker ────────────────────────────────────── */

interface JurisdictionPickerProps {
  groups: typeof JG;
  selected: string[];
  onChange: (selected: string[]) => void;
  onCompare: () => void;
  disabled?: boolean;
  onDismiss?: () => void;
}

export function SbsJurisdictionPicker({ groups, selected, onChange, onCompare, disabled, onDismiss }: JurisdictionPickerProps) {
  const toggle = (id: string) => {
    if (selected.includes(id)) {
      onChange(selected.filter(s => s !== id));
    } else if (selected.length < 4) {
      onChange([...selected, id]);
    }
  };

  // ESC to dismiss
  useEffect(() => {
    if (!onDismiss) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onDismiss]);

  return (
    <div className="sbs-picker">
      <div className="sbs-picker__header">
        Select jurisdictions to compare <span className="sbs-picker__count">({selected.length}/4)</span>
        {onDismiss && (
          <button className="sbs-picker__close" onClick={onDismiss} aria-label="Close" type="button">✕</button>
        )}
      </div>
      <div className="sbs-picker__groups">
        {groups.map(group => (
          <div key={group.name} className="sbs-picker__group">
            <div className="sbs-picker__group-label">{group.icon} {group.name}</div>
            <div className="sbs-picker__chips">
              {group.targets.map(t => (
                <button
                  key={t.id}
                  className={`sbs-picker__chip${selected.includes(t.id) ? ' sbs-picker__chip--selected' : ''}${!selected.includes(t.id) && selected.length >= 4 ? ' sbs-picker__chip--disabled' : ''}`}
                  onClick={() => toggle(t.id)}
                  disabled={!selected.includes(t.id) && selected.length >= 4}
                  title={t.label}
                >
                  {selected.includes(t.id) ? '✓ ' : ''}{t.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <button
        className="sbs-picker__compare-btn"
        disabled={disabled || selected.length < 2}
        onClick={onCompare}
      >
        ⚖️ Compare ({selected.length}) →
      </button>
    </div>
  );
}

/* ── Results Column ─────────────────────────────────────────── */

interface SbsColumnProps {
  result: SbsJurisdictionResult;
  onCitationClick?: (citation: Citation) => void;
}

function SbsColumn({ result, onCitationClick }: SbsColumnProps) {
  const contentRef = useRef<HTMLDivElement>(null);

  // Auto-scroll during streaming
  useEffect(() => {
    if (result.status === 'streaming' && contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [result.content, result.status]);

  const confidenceClass = result.confidence
    ? `sbs-column__confidence--${result.confidence.toLowerCase()}`
    : '';

  return (
    <div className={`sbs-column sbs-column--${result.status}`}>
      <div className="sbs-column__header">
        <span className="sbs-column__label">{result.label}</span>
        {result.confidence && (
          <span className={`sbs-column__confidence ${confidenceClass}`} title={result.confidenceReason || ''}>
            {result.confidence}
          </span>
        )}
        {result.status === 'streaming' && <span className="sbs-column__spinner" />}
        {result.status === 'queued' && <span className="sbs-column__queued">Queued</span>}
        {result.status === 'searching' && <span className="sbs-column__searching">Searching…</span>}
      </div>
      <div className="sbs-column__body" ref={contentRef}>
        {result.status === 'queued' || result.status === 'searching' ? (
          <div className="sbs-column__skeleton">
            <div className="sbs-column__skeleton-line sbs-column__skeleton-line--long" />
            <div className="sbs-column__skeleton-line sbs-column__skeleton-line--medium" />
            <div className="sbs-column__skeleton-line sbs-column__skeleton-line--short" />
            <div className="sbs-column__skeleton-line sbs-column__skeleton-line--long" />
          </div>
        ) : result.error ? (
          <div className="sbs-column__error">⚠️ {result.error}</div>
        ) : (
          <div className="sbs-column__content markdown-body"><ReactMarkdown remarkPlugins={[remarkGfm]}>{result.content}</ReactMarkdown></div>
        )}
      </div>
      {result.citations.length > 0 && (
        <div className="sbs-column__citations">
          <div className="sbs-column__citations-label">Sources ({result.citations.length})</div>
          <div className="sbs-column__citations-list">
            {result.citations.slice(0, 5).map(cit => (
              <button
                key={cit.id}
                className="sbs-column__citation-chip"
                onClick={() => onCitationClick?.(cit)}
                title={`${cit.standard} ${cit.clause}`}
              >
                [{cit.number}] {cit.standard} {cit.clause}
              </button>
            ))}
            {result.citations.length > 5 && (
              <span className="sbs-column__citations-more">+{result.citations.length - 5} more</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Main Comparison View ───────────────────────────────────── */

interface SbsComparisonViewProps {
  state: SbsComparisonState;
  onCitationClick?: (citation: Citation) => void;
  onClose?: () => void;
}

export default function SbsComparisonView({ state, onCitationClick, onClose }: SbsComparisonViewProps) {
  const [activeTab, setActiveTab] = useState(0);
  const results = Array.from(state.results.values());
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  // Update active tab when jurisdictions change
  useEffect(() => {
    if (activeTab >= results.length && results.length > 0) {
      setActiveTab(0);
    }
  }, [results.length, activeTab]);

  const anyStreaming = results.some(r => r.status === 'streaming' || r.status === 'searching');
  const allDone = results.every(r => r.status === 'complete' || r.status === 'error');

  return (
    <div className="sbs-view">
      {/* Header */}
      <div className="sbs-view__header">
        <div className="sbs-view__title">
          <span className="sbs-view__icon">⚖️</span>
          <span>Jurisdiction Comparison</span>
          {state.synthesisConfidence && (
            <span className={`sbs-view__confidence sbs-view__confidence--${state.synthesisConfidence.toLowerCase()}`}>
              {state.synthesisConfidence}
            </span>
          )}
        </div>
        <div className="sbs-view__actions">
          {(anyStreaming || state.synthesisStatus === 'generating') && (
            <span className="sbs-view__loading">
              {state.synthesisStatus === 'generating' ? 'Synthesising comparison…' : 'Analysing jurisdictions…'}
            </span>
          )}
          {onClose && (
            <button className="sbs-view__close" onClick={onClose} title="Close comparison">✕</button>
          )}
        </div>
      </div>

      {/* Mobile tab bar */}
      {isMobile && results.length > 0 && (
        <div className="sbs-view__tabs">
          {results.map((r, i) => (
            <button
              key={r.jurisdictionId}
              className={`sbs-view__tab${i === activeTab ? ' sbs-view__tab--active' : ''}`}
              onClick={() => setActiveTab(i)}
            >
              {r.label}
              {r.status === 'streaming' && <span className="sbs-view__tab-dot" />}
            </button>
          ))}
          {state.synthesisStatus !== 'idle' && (
            <button
              className={`sbs-view__tab${activeTab === results.length ? ' sbs-view__tab--active' : ''}`}
              onClick={() => setActiveTab(results.length)}
            >
              Comparison
              {state.synthesisStatus === 'generating' && <span className="sbs-view__tab-dot" />}
            </button>
          )}
        </div>
      )}

      {/* Desktop: side-by-side grid */}
      {!isMobile && results.length > 0 && (
        <div className="sbs-view__grid" style={{ gridTemplateColumns: `repeat(${results.length}, 1fr)` }}>
          {results.map(r => (
            <SbsColumn key={r.jurisdictionId} result={r} onCitationClick={onCitationClick} />
          ))}
        </div>
      )}

      {/* Mobile: single column via tab */}
      {isMobile && results.length > 0 && activeTab < results.length && (
        <div className="sbs-view__mobile-content">
          <SbsColumn result={results[activeTab]} onCitationClick={onCitationClick} />
        </div>
      )}

      {/* Synthesis section */}
      {(state.synthesisStatus !== 'idle' || state.synthesis) && (
        <div className={`sbs-view__synthesis${isMobile && activeTab !== results.length ? ' sbs-view__synthesis--hidden' : ''}`}>
          <div className="sbs-view__synthesis-header">
            <span>📊 Cross-Jurisdiction Analysis</span>
            {state.synthesisStatus === 'generating' && <span className="sbs-column__spinner" />}
          </div>
          <div className="sbs-view__synthesis-body markdown-body"><ReactMarkdown remarkPlugins={[remarkGfm]}>{state.synthesis}</ReactMarkdown></div>
        </div>
      )}

      {/* Error */}
      {state.error && (
        <div className="sbs-view__error">
          ⚠️ {state.error}
        </div>
      )}
    </div>
  );
}
